import baseApi, { GenericResponse } from 'services/api/base';
import getProductKey from 'helper/requests/getProductKey';
import { GetCities } from 'types/Meteorology/Agro/getCities';
import {
  transformGetAgroLocationsResponse,
  transformGetAgroLocationGroupsResponse,
  transformGetCitiesResponse,
} from 'helper/Meteorology/Agro';

import type Location from 'types/Locations/Location';
import type Group from 'types/Locations/Group';

export const agroApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAgroCities: build.query<GetCities[], void>({
      query: () => {
        const productKey = getProductKey('agro');
        return {
          url: `produtos/agro/cidade/list?product_key=${productKey}`,
          method: 'GET',
        };
      },
      transformResponse: transformGetCitiesResponse,
    }),
    getAgroGroups: build.query<Group[], void>({
      query: () => {
        const productKey = getProductKey('agro');
        return {
          url: `produtos/agro/balanco-hidrico/grupo/list?product_key=${productKey}`,
          method: 'GET',
        };
      },
      transformResponse: transformGetAgroLocationGroupsResponse,
      providesTags: ['AgroGroups'],
    }),
    getAgroLocations: build.query<Location[], void>({
      query: () => {
        const productKey = getProductKey('agro');
        return {
          url: `produtos/agro/balanco-hidrico/local/list?product_key=${productKey}`,
          methood: 'GET',
        };
      },
      transformResponse: transformGetAgroLocationsResponse,
      providesTags: ['AgroLocations'],
    }),
    saveAgroGroup: build.mutation<GenericResponse<any>, { name, description }>({
      query: ({ name, description }) => {
        const productKey = getProductKey('agro');

        return {
          url: `produtos/agro/balanco-hidrico/grupo/create?product_key=${productKey}`,
          method: 'POST',
          body: {
            nome: name,
            descricao: description,
          },
        };
      },
      invalidatesTags: ['AgroGroups'],
    }),
    updateAgroGroup: build.mutation<GenericResponse<any>, { id, name }>({
      query: ({
        id, name,
      }) => {
        const productKey = getProductKey('agro');

        return {
          url: `produtos/agro/balanco-hidrico/grupo/update?product_key=${productKey}`,
          method: 'POST',
          body: {
            id,
            nome: name,
          },
        };
      },
      invalidatesTags: ['AgroGroups'],
    }),
    saveAgroLocation: build.mutation<GenericResponse<any>,
    { groupId, name, latitude, longitude, cityId }>({
      query: ({
        groupId, name, latitude, longitude, cityId,
      }) => {
        const productKey = getProductKey('agro');
        return {
          url: `produtos/agro/balanco-hidrico/local/create?product_key=${productKey}`,
          method: 'POST',
          body: {
            grupo_id: groupId,
            nome: name,
            latitude,
            longitude,
            city_id: cityId,
          },
        };
      },
      invalidatesTags: ['AgroLocations'],
    }),
    updateAgroLocation: build.mutation<GenericResponse<any>, { id, name }>({
      query: ({
        id, name,
      }) => {
        const productKey = getProductKey('agro');

        return {
          url: `produtos/agro/balanco-hidrico/local/update?product_key=${productKey}`,
          method: 'POST',
          body: {
            id,
            nome: name,
          },
        };
      },
      invalidatesTags: ['AgroLocations'],
    }),
    deleteAgroGroup: build.mutation<GenericResponse<null>, { id }>({
      query: ({ id }) => {
        const productKey = getProductKey('agro');

        return {
          url: `produtos/agro/balanco-hidrico/grupo/delete?product_key=${productKey}`,
          method: 'DELETE',
          body: {
            id,
          },
        };
      },
      invalidatesTags: ['AgroGroups'],
    }),
    deleteAgroLocation: build.mutation<GenericResponse<null>, { id }>({
      query: ({ id }) => {
        const productKey = getProductKey('agro');

        return {
          url: `produtos/agro/balanco-hidrico/local/delete?product_key=${productKey}`,
          method: 'DELETE',
          body: {
            id,
          },
        };
      },
      invalidatesTags: ['AgroLocations'],
    }),
  }),
});

export const {
  useGetAgroCitiesQuery,
  useGetAgroGroupsQuery,
  useGetAgroLocationsQuery,
  useSaveAgroGroupMutation,
  useSaveAgroLocationMutation,
  useUpdateAgroGroupMutation,
  useUpdateAgroLocationMutation,
  useDeleteAgroGroupMutation,
  useDeleteAgroLocationMutation,
} = agroApi;
